import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    useNavigate,
    useParams,
    useSearchParams,
    useLocation,
} from 'react-router-dom';

import FormBase from 'components/forms/new/FormBase';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';
import useFieldValidation from 'hooks/useFieldValidation';
import { customTabStyles, selectedTabStyles } from '@/constants';
import OCProfilesDropdown from './components/OCProfilesDropdown';
import ConsentsTable from './components/ConsentsTable';
import { formContainerStyles, tabContentStyles } from './constants';
import OperatorProfileField from './formFields/OperatorProfileField';
import ReadOnlyProfileField from './formFields/ReadOnlyProfileField';
import { useGetOCProfile, useUpdateOCProfile, useGetRegions } from './queries';
import { ocProfileSchema } from './schema';
import OperatorConnectRegionsField from './formFields/OCRegionsField';
import { multiSelectDataMapper } from 'utils/utils';

const OCProfileEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { profileId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { darkMode } = useSelector((state) => state.settings);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { data: profile, isLoading } = useGetOCProfile(profileId);
    const { data: regionsData } = useGetRegions(currentWholesaler);
    const { mutate: updateProfile, isLoading: isUpdating } =
        useUpdateOCProfile();
    const [error, setError] = useState(null);
    const [regions, setRegions] = useState([]);

    useEffect(() => {
        if (profile) {
            setRegions(profile.regions?.map(multiSelectDataMapper) || []);
        }
    }, [profile]);

    const breadCrumbTrail = [
        {
            label: 'All Operator Profiles',
            path: '/operator-connect-profiles',
        },
        {
            label: <OCProfilesDropdown currentId={profileId} />,
            path: undefined,
        },
    ];

    const { validatefn } = useFieldValidation({ regions }, ocProfileSchema);

    const handleSubmit = (values, actions) => {
        if (!validatefn()) {
            actions.setSubmitting(false);
            return;
        }

        setError(null);
        updateProfile(
            { ...profile, ...values, regions },
            {
                onSuccess: () => {
                    navigate(
                        `/operator-connect-profiles/${profileId}${location.search}`,
                    );
                },
                onError: (err) => {
                    setError(
                        err?.response?.data?.message || 'An error occurred.',
                    );
                    actions.setSubmitting(false);
                },
            },
        );
    };

    if (isLoading || !profile) {
        return <LoadingIndicator />;
    }

    const tabParam = searchParams.get('tab') || 'details';
    const tabIndex = tabParam === 'consents' ? 1 : 0;

    const handleTabChange = (index) => {
        setSearchParams({ tab: index === 1 ? 'consents' : 'details' });
    };

    const formBaseStyles = {
        ...tabContentStyles,
        ...(tabIndex === 0 ? formContainerStyles.noHorizontalDivider : {}),
    };

    return (
        <Box
            sx={formBaseStyles}
            className={tabIndex === 1 ? 'is-consents-tab' : ''}>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                initialValues={{
                    name: profile.name || '',
                    carrierName: profile.carrierName || '',
                    appID: profile.appID || '',
                    tenantID: profile.tenantID || '',
                    clientSecret: profile.clientSecret || '',
                    regions: profile.regions || [],
                }}
                enableReinitialize
                finalFormSubmit={handleSubmit}
                backLink="/operator-connect-profiles"
                isLoading={isUpdating}
                tabMode={false}
                subTitleHeader
                subTitleHeaderComponent={
                    <Tabs
                        index={tabIndex}
                        onChange={handleTabChange}
                        variant="unstyled">
                        <TabList borderBottom={0} mb="10px">
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Operator Profile Details
                            </Tab>
                            <Tab
                                sx={customTabStyles}
                                _selected={selectedTabStyles}>
                                Consents
                            </Tab>
                        </TabList>
                    </Tabs>
                }>
                {(formProps) => {
                    const { errors, touched, actions } = formProps;

                    if (tabIndex === 1) {
                        return <ConsentsTable profileId={profileId} />;
                    }

                    return [
                        <OperatorProfileField
                            key="name"
                            fieldName="name"
                            label="Profile Name"
                            placeholder="Enter profile name"
                            errorMessage="Profile Name is required"
                            tooltipLabel="Enter the operator profile name."
                            tooltipAriaLabel="profile name tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorConnectRegionsField
                            key="regions"
                            darkMode={darkMode}
                            value={regions}
                            onChange={setRegions}
                            options={
                                regionsData?.map(multiSelectDataMapper) || []
                            }
                            errors={errors}
                            touched={touched}
                            error={
                                !regions || regions.length === 0
                                    ? 'At least one region is required'
                                    : ''
                            }
                            showError={actions?.isSubmitting}
                            disableStackHeight
                            isInvalid={false}
                        />,
                        <OperatorProfileField
                            key="clientSecret"
                            fieldName="clientSecret"
                            label="Client Secret"
                            placeholder="Enter Client Secret"
                            tooltipLabel="Enter the client secret for Azure authentication."
                            tooltipAriaLabel="client secret tooltip"
                            isRequired={false}
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="carrierName"
                            fieldName="carrierName"
                            label="Carrier Name"
                            placeholder="Enter carrier name"
                            errorMessage="Carrier Name is required"
                            tooltipLabel="Enter the carrier name for this operator profile."
                            tooltipAriaLabel="carrier name tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="appID"
                            fieldName="appID"
                            label="App ID"
                            placeholder="Enter App ID"
                            errorMessage="App ID is required"
                            tooltipLabel="Enter the Azure App ID for this operator profile."
                            tooltipAriaLabel="app id tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <OperatorProfileField
                            key="tenantID"
                            fieldName="tenantID"
                            label="Tenant ID"
                            placeholder="Enter Tenant ID"
                            errorMessage="Tenant ID is required"
                            tooltipLabel="Enter the Azure Tenant ID for this operator profile."
                            tooltipAriaLabel="tenant id tooltip"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                        />,
                        <ReadOnlyProfileField
                            key="lastSync"
                            label="Last Sync"
                            value={profile.lastSync}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="totalConsents"
                            label="Total Consents"
                            value={profile.totalConsents}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="status"
                            label="Status"
                            value={profile.status}
                            darkMode={darkMode}
                        />,
                        <ReadOnlyProfileField
                            key="callingProfiles"
                            label="Calling Profiles"
                            value={profile.callingProfiles}
                            darkMode={darkMode}
                            isTextarea
                        />,
                        error && (
                            <Box key="error-box" paddingBottom="0.5rem">
                                <Alert
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ];
                }}
            </FormBase>
        </Box>
    );
};

export default OCProfileEdit;
